import {
  AppointmentCalendarDto,
  AppointmentStatus,
  WorkScheduleListData,
  UserAppointmentsDto,
} from "@/models/club";
import moment from "moment-jalaali";
import { CalendarEvent } from "../types";

// Helper function to check if a partner has a shift at a specific hour
export const hasShiftAtHour = (
  shiftSchedule: WorkScheduleListData,
  currentDate: moment.Moment,
  partnerId: number | undefined,
  hour: number
): boolean => {
  if (!shiftSchedule || !partnerId || !currentDate) return true; // Default to true if no shift data

  const partnerShift = shiftSchedule.find(
    (shift) => shift?.user?.id === partnerId
  );

  if (!partnerShift || !partnerShift.workScheduleCalendar) return false;

  const todaySchedule = partnerShift.workScheduleCalendar.find((calendar) => {
    const scheduleDate = moment(calendar.availableDate);
    return scheduleDate.isSame(currentDate, "day");
  });

  if (
    !todaySchedule ||
    !todaySchedule.timeSlots ||
    todaySchedule.timeSlots.length === 0
  ) {
    return false;
  }

  // Check if any timeSlot covers this hour
  return todaySchedule.timeSlots.some((slot) => {
    const startHour = moment(slot.startTime, "HH:mm:ss").hour();
    const endHour = moment(slot.endTime, "HH:mm:ss").hour();

    // Handle cases where shift crosses midnight
    if (endHour < startHour) {
      return hour >= startHour || hour < endHour;
    }

    return hour >= startHour && hour < endHour;
  });
};

// Same result as hasShiftAtHour for hours 0..23, with the member/day lookup and slot parsing done once.
export const getShiftHours = (
  shiftSchedule: WorkScheduleListData,
  currentDate: moment.Moment,
  partnerId: number | undefined
): boolean[] => {
  const hours = Array<boolean>(24).fill(false);
  if (!shiftSchedule || !partnerId || !currentDate) return hours.fill(true);
  const partnerShift = shiftSchedule.find((shift) => shift?.user?.id === partnerId);
  const todaySchedule = partnerShift?.workScheduleCalendar?.find((calendar) =>
    moment(calendar.availableDate).isSame(currentDate, "day"));
  for (const slot of todaySchedule?.timeSlots || []) {
    const startHour = moment(slot.startTime, "HH:mm:ss").hour();
    const endHour = moment(slot.endTime, "HH:mm:ss").hour();
    for (let hour = 0; hour < 24; hour++) {
      if (endHour < startHour ? hour >= startHour || hour < endHour : hour >= startHour && hour < endHour) hours[hour] = true;
    }
  }
  return hours;
};

// Transform appointmentCalendar to events for each partner for the current day
export const getPartnerDayEvents = (
  appointmentCalendar: AppointmentCalendarDto[] | null | undefined,
  currentDate: moment.Moment,
  partner: UserAppointmentsDto
): CalendarEvent[] => {
  if (!appointmentCalendar || !Array.isArray(appointmentCalendar)) {
    return [];
  }

  const events: CalendarEvent[] = [];

  appointmentCalendar.forEach((calendarDay) => {
    const dayDate = moment(calendarDay.appointmentDate);

    // Only process events for the current day
    if (!dayDate.isSame(currentDate, "day")) {
      return;
    }

    calendarDay.appointments?.forEach((appointment) => {
      events.push({
        id: appointment.id,
        appointmentDate: calendarDay.appointmentDate as string | undefined,
        merchantUserColor: partner?.user?.calendarColor || "",
        merchantServiceTitle: appointment.merchantServiceTitle || "",
        merchantServiceColor: appointment.merchantServiceColor || "",
        merchantUserId: appointment.merchantUserId,
        merchantUserFirstName: appointment.merchantUserFirstName || "",
        merchantUserLastName: appointment.merchantUserLastName || "",
        customerId: appointment.customerId,
        customerFirstName: appointment.customerFirstName || "",
        customerLastName: appointment.customerLastName || "",
        customerMobileNumber: appointment.customerMobileNumber || "",
        description: appointment.description || "",
        status:
          (appointment.status as unknown as AppointmentStatus) ||
          AppointmentStatus.Value0,
        startTime: appointment.startTime,
        endTime: appointment.endTime,
        actor: appointment?.actor,
        merchantServiceId: appointment?.merchantServiceId,
        rateBreakdown: appointment?.rateBreakdown,
        cancellation: appointment?.cancellation,
      });
    });
  });

  return events;
};

// Function to scroll to current time (rounds to 10-min block since DOM ids are per block)
export const scrollToCurrentTime = (
  scrollAreaRef: React.RefObject<HTMLDivElement>
) => {
  const now = moment();
  const minute = now.minute();
  const roundedMinute = Math.floor(minute / 10) * 10;
  const blockTimeString = now.clone().minute(roundedMinute).second(0).format("HH:mm");
  scrollToTime(scrollAreaRef, blockTimeString);
};

// Function to scroll to a specific time (e.g. "09:00")
export const scrollToTime = (
  scrollAreaRef: React.RefObject<HTMLDivElement>,
  timeString: string
) => {
  const currentTimeElement = document.getElementById(
    `current-time-hour-${timeString}`
  );

  if (currentTimeElement && scrollAreaRef.current) {
    let scrollContainer = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]"
    );

    if (!scrollContainer) {
      scrollContainer = scrollAreaRef.current.querySelector(
        ".mantine-ScrollArea-viewport"
      );
    }

    if (!scrollContainer) {
      scrollContainer = scrollAreaRef.current.querySelector(
        "div[style*='overflow']"
      );
    }

    if (!scrollContainer) {
      scrollContainer = scrollAreaRef.current;
    }

    if (scrollContainer) {
      const elementRect = currentTimeElement.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const containerHeight = containerRect.height;
      const elementHeight = elementRect.height;

      const scrollTop =
        scrollContainer.scrollTop +
        elementRect.top -
        containerRect.top -
        containerHeight / 2 +
        elementHeight / 2;

      scrollContainer.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: "smooth",
      });
    }
  }
};
